import { isArray } from 'lodash';
import React, { Component } from 'react';
import styleable from 'react-styleable';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import { DropdownMenu, Button, Header, Footer } from '@3plearning/react-components';
import css from './certificates.scss';
import modalActions from '../../components/modal/modal-actions';
import studentCertificatesActions, { STUDENT_CERTIFICATES_RETRIEVED } from './student-certificates-actions';
import { MATHLETICS_PRODUCT_ID } from '../../helpers/backend-constants-contract';
import CertificateTemplate from '../../components/certificate-template/certificate-template';
import { SECONDARY, PRIMARY } from '../../components/certificate-template/certificate-template-constants';
import {
    ALL_STUDENTS,
    BRONZE_CERT_TYPE,
    SILVER_CERT_TYPE,
    GOLD_CERT_TYPE,
    PLATINUM_CERT_TYPE,
    EMERALD_CERT_TYPE,
    DIAMOND_CERT_TYPE,
    EPIC_CERT_TYPE,
    LEGEND_CERT_TYPE,
    GOLD_CERT_TYPE_PAST,
    SILVER_CERT_TYPE_PAST,
    BRONZE_CERT_TYPE_PAST,
    CERT_VALUES
} from './certificates-constants';
import {
    PRINT_FRIENDLY,
    FULL_COLOR, ALL_PAST, ALL_NEW
} from './download-constants';
import datePickerActions from '../../components/date-picker/date-picker-actions';
import downloadActions from './download-certificates-actions';
import { DatePicker } from '../../components/date-picker/date-picker';
import { DEFAULT_DATE_OPTIONS_OBJ }
    from '../../components/date-picker/components/date-picker-trans/date-picker-constants';
import CertificatesWrapper from '../../components/certificates-wrapper/certificates-wrapper';
import DownloadIcon from '../../assets/images/download-icon.svg';
import { isUndefined } from '../../helpers/utils';
import studentCertificatesGemCountActions,
{ STUDENT_CERTIFICATES_GEM_COUNT_RETRIEVED } from './student-certificates-gem-count-actions';
import { formatDateForGemCount } from '../../helpers/generate-certificate-filename';

/**
 * Certificates Redux component
 * Receives properties via 'props'
 * Displays certificates page for a student (or all students) with totals for each type of
 * certificate (gold, silver, bronze) and option to download them by type or all in one
 */
export class Certificates extends Component {
    static contextTypes = {
        router: PropTypes.object
    };

    constructor(props) {
        super(props);
        this.state = {
            studentList: [],
            selectedOption: null,
            templates: {
                certificates: [],
                pastCertificates: []
            },
            studentTotalNewCertificates: null,
            studentTotalPastCertificates: null,
            options: DEFAULT_DATE_OPTIONS_OBJ,
            gemsSelected: null
        };
    }

    componentDidMount() {
        const { history } = this.context.router;
        const { selectedDateRange, selectedClassroom, selectedStudent } = this.props;

        if (!selectedClassroom || !selectedDateRange.startDate) {
            history.push('/home');
        } else {
            this.setSelectedOption(selectedStudent);
            this.getStudentCertificates(selectedDateRange.startDate, selectedDateRange.endDate);
        }
    }

    componentWillReceiveProps(newProps) {
        const { selectedStudent, studentCertificates, selectedDateRange, studentCertificatesGemCount } = this.props;
        const {
            selectedStudent: newSelectedStudent,
            studentCertificates: newStudentCertificates,
            selectedDateRange: newSelectedDateRange,
            studentCertificatesGemCount: newStudentCertificatesGemCount
        } = newProps;

        if (studentCertificates !== newStudentCertificates) {
            this.setCertificateCounts(newStudentCertificates);
            this.setStudentList(newStudentCertificates.studentList);
        }
        if (newSelectedStudent !== selectedStudent) {
            this.setSelectedOption(newSelectedStudent);
        }
        if (selectedDateRange !== newSelectedDateRange) {
            this.getStudentCertificates(newSelectedDateRange.startDate, newSelectedDateRange.endDate);
        }

        if (studentCertificatesGemCount.selectedGems !== newStudentCertificatesGemCount.selectedGems) {
            this.setState({ gemsSelected: newStudentCertificatesGemCount.selectedGems.result });
        }
    }

    getStudentCertificates(startDate, endDate) {
        const { selectedClassroom, getStudentCertificates } = this.props;

        getStudentCertificates({
            classroomId: selectedClassroom.id,
            startDate,
            endDate,
            productId: MATHLETICS_PRODUCT_ID
        }).then(
            (action) => {
                if (action.type !== STUDENT_CERTIFICATES_RETRIEVED) {
                    this.displayError(action.error);
                }
            },
            error => this.displayError(error)
        );
    }

    async getStudentCertificatesGemCount(startDate, endDate, selectedStudent, certificateRank) {
        const { selectedClassroom, getStudentCertificatesGemCount } = this.props;
        await getStudentCertificatesGemCount({
            classroomId: selectedClassroom.id,
            startDate: formatDateForGemCount(startDate),
            endDate: formatDateForGemCount(endDate),
            groupId: 0,
            studentUserProfileId: selectedStudent,
            certificateType: certificateRank
        }).then(
            (action) => {
                if (action.type !== STUDENT_CERTIFICATES_GEM_COUNT_RETRIEVED) {
                    this.displayError(action.error);
                }
            },
            error => this.displayError(error)
        );
    }

    setStudentList(studentList) {
        const { localeData } = this.props;
        this.setState({
            studentList: [
                { label: localeData['Label - AllStudents'], value: ALL_STUDENTS },
                ...studentList.map(student => (
                    { label: `${student.firstName} ${student.lastName}`, value: student.id }
                ))
            ]
        });
    }

    setSelectedOption(selectedStudent) {
        this.setState({
            selectedOption: {
                label: selectedStudent.student, value: selectedStudent.id
            }
        });
    }

    setCertificateCounts(studentCertificates) {
        const { selectedOption } = this.state;
        let selectedCertificateInfo;
        const isAllStudents = selectedOption.value === ALL_STUDENTS;
        if (isAllStudents) {
            selectedCertificateInfo = studentCertificates.allStudents;
        } else {
            selectedCertificateInfo = studentCertificates.studentList.find(
                student => student.id === selectedOption.value);
        }
        const { localeData } = this.props;
        if (!selectedCertificateInfo) {
            this.setState({ templates: { certificates: [], pastCertificates: [] } });
            this.displayError(`${localeData['Empty - StudentCertificate']} (${selectedOption.label})`);
        } else {
            this.setState({
                templates: {
                    certificates: [
                        {
                            type: BRONZE_CERT_TYPE,
                            total: selectedCertificateInfo.certificates.bronze,
                            label: localeData['Label - Bronze'],
                            isAllStudents
                        },
                        {
                            type: SILVER_CERT_TYPE,
                            total: selectedCertificateInfo.certificates.silver,
                            label: localeData['Label - Silver'],
                            isAllStudents
                        },
                        {
                            type: GOLD_CERT_TYPE,
                            total: selectedCertificateInfo.certificates.gold,
                            label: localeData['Label - Gold'],
                            isAllStudents
                        },
                        {
                            type: PLATINUM_CERT_TYPE,
                            total: selectedCertificateInfo.certificates.platinum,
                            label: localeData['Label - Platinum'],
                            isAllStudents
                        },
                        {
                            type: EMERALD_CERT_TYPE,
                            total: selectedCertificateInfo.certificates.emerald,
                            label: localeData['Label - Emerald'],
                            isAllStudents
                        },
                        {
                            type: DIAMOND_CERT_TYPE,
                            total: selectedCertificateInfo.certificates.diamond,
                            label: localeData['Label - Diamond'],
                            isAllStudents
                        },
                        {
                            type: EPIC_CERT_TYPE,
                            total: selectedCertificateInfo.certificates.epic,
                            label: localeData['Label - Epic'],
                            isAllStudents
                        },
                        {
                            type: LEGEND_CERT_TYPE,
                            total: selectedCertificateInfo.certificates.legend,
                            label: localeData['Label - Legend'],
                            isAllStudents
                        }
                    ],
                    pastCertificates: [
                        {
                            type: BRONZE_CERT_TYPE_PAST,
                            total: selectedCertificateInfo.pastCertificates.bronze,
                            label: localeData['Label - Bronze']
                        },
                        {
                            type: SILVER_CERT_TYPE_PAST,
                            total: selectedCertificateInfo.pastCertificates.silver,
                            label: localeData['Label - Silver']
                        },
                        {
                            type: GOLD_CERT_TYPE_PAST,
                            total: selectedCertificateInfo.pastCertificates.gold,
                            label: localeData['Label - Gold']
                        }
                    ]
                },
                studentTotalNewCertificates: selectedCertificateInfo.certificates.total,
                studentTotalPastCertificates: selectedCertificateInfo.pastCertificates.total
            });
        }
    }

    selectOption = (option) => {
        this.setState(
            { selectedOption: { ...option } },
            () => { this.setCertificateCounts(this.props.studentCertificates); }
        );
    }

    displayError = (error) => {
        const { localeData } = this.props;
        this.props.openModal({
            title: localeData['Modal - Oops'],
            content: <p>{error}</p>,
            buttons: [{ title: localeData['Modal - TryAgain'], primary: true }]
        });
    };

    /**
     * Downloads pdf certificates for a particular type (gold, silver, bronze)
     * Either for the current student or all students, depending on the selection in student name dropdown
     */
    downloadPastCertificatesByType = ({ certificateType, downloadType }) => {
        const { productId, selectedClassroom, selectedDateRange, downloadPastCertsByType } = this.props;
        const { selectedOption } = this.state;

        downloadPastCertsByType({
            certificateType,
            paramData: {
                userProfileId: selectedOption.value,
                productId,
                classroomId: selectedClassroom.id,
                classroomName: selectedClassroom.class,
                startDate: selectedDateRange.startDate,
                endDate: selectedDateRange.endDate,
                studentName: this.getStudentName()
            },
            downloadType
        });
    }

    /**
     * Downloads pdf Past Certificates for all types (gold, silver, bronze) in one file
     * Either for the current student or all students, depending on the selection in student name dropdown
     */
    downloadAllPastCertificates = () => {
        const {
            productId,
            selectedClassroom,
            selectedDateRange,
            downloadAllPastCerts } = this.props;
        const { selectedOption } = this.state;

        downloadAllPastCerts({
            userProfileId: selectedOption.value,
            productId,
            classroomId: selectedClassroom.id,
            startDate: selectedDateRange.startDate,
            endDate: selectedDateRange.endDate,
            classroomName: selectedClassroom.class,
            studentName: this.getStudentName(),
            downloadType: ALL_PAST
        });
    }

    certificateTypeName = (certificateRank) => {
        const { localeData } = this.props;
        switch (certificateRank) {
        case 1:
            return localeData['Label - Bronze'];
        case 2:
            return localeData['Label - Silver'];
        case 3:
            return localeData['Label - Gold'];
        case 4:
            return localeData['Label - Platinum'];
        case 5:
            return localeData['Label - Emerald'];
        case 6:
            return localeData['Label - Diamond'];
        case 7:
            return localeData['Label - Epic'];
        case 8:
            return localeData['Label - Legend'];
        default:
            return '';
        }
    };

    showPrintTypeModal = async ({
        content,
        total,
        certificateRank = null,
        isDownloadCertificateType
    }) => {
        const {
            selectedDateRange,
            selectedClassroom,
            localeData
        } = this.props;
        const { selectedOption } = this.state;
        const certificateType = this.certificateTypeName(certificateRank);
        await this.getStudentCertificatesGemCount(
            selectedDateRange.startDate,
            selectedDateRange.endDate,
            selectedOption.value,
            certificateRank
        );
        const { studentCertificatesGemCount } = this.props;
        const selectedGems = studentCertificatesGemCount.retrievedGems.map(() => true)
            .map((_, selectedIndex) => (selectedIndex))
            .filter(selectedIndex => selectedIndex !== -1);
        const result = selectedGems
            .map(gemType => `gemTypes: ${gemType + 1}`)
            .join(', ');
        this.props.downloadCertificatesByGem({
            payload: {
                selectedGems: { result, resultCount: total }
            }
        });
        const label = `${selectedClassroom.class} | ${selectedOption.label}`;
        const certificatesCount = isArray(studentCertificatesGemCount.retrievedGems) ?
            studentCertificatesGemCount.retrievedGems.reduce((acc, item) => acc + item.count, 0) : null;

        this.props.openModal({
            title: `${total} ${total === 1 ? localeData['Label - Certificate'] : localeData['Label - Certificates']}`,
            content,
            label,
            dateRange: selectedDateRange.type,
            certificatesCount,
            retrievedGems: studentCertificatesGemCount.retrievedGems,
            certificateType,
            isDownloadCertificateType,
            buttons: [
                {
                    title: `${localeData['Label - FullColour']}`,
                    primary: true,
                    icon: DownloadIcon,
                    btnType: FULL_COLOR,
                    handler: () => this.downloadNewCertificates({
                        downloadType: FULL_COLOR,
                        certificateRank
                    }),
                    disabled: certificatesCount === 0
                },
                {
                    title: `${localeData['Label - PrintFriendly']}`,
                    primary: true,
                    icon: DownloadIcon,
                    btnType: PRINT_FRIENDLY,
                    handler: () => this.downloadNewCertificates({
                        downloadType: PRINT_FRIENDLY,
                        certificateRank
                    }),
                    disabled: certificatesCount === 0
                }],
            useNewModal: true
        });
    }

    downloadNewCertificates = ({ downloadType, certificateRank }) => {
        const {
            productId,
            selectedClassroom,
            selectedDateRange,
            downloadAllNewCerts,
            studentCertificates,
            downloadNewCertsByType
        } = this.props;
        const { selectedOption, gemsSelected } = this.state;
        const userId = selectedOption.value === ALL_STUDENTS ?
            null : studentCertificates.studentList.find(student => student.id === selectedOption.value).userId;
        const userProfileId = selectedOption.value;
        const dataObj = {
            userProfileId,
            productId,
            classroomId: selectedClassroom.id,
            startDate: selectedDateRange.startDate,
            endDate: selectedDateRange.endDate,
            userId,
            downloadType,
            classroomName: selectedClassroom.class,
            studentName: this.getStudentName(),
            fullColor: downloadType === FULL_COLOR,
            gemTypes: gemsSelected
        };
        const isAllNewCerts = isUndefined(certificateRank);
        const isClassLevel = userProfileId === ALL_STUDENTS;
        if (isAllNewCerts) {
            dataObj.isClassLevel = isClassLevel;
            downloadAllNewCerts(dataObj);
        } else {
            dataObj.certificateRank = certificateRank;
            downloadNewCertsByType(dataObj);
        }
    }

    onBackClick = () => {
        const { history } = this.context.router;
        history.goBack();
    };

    getStudentName = () => {
        const { selectedOption } = this.state;
        const { studentCertificates } = this.props;

        if (selectedOption.value === ALL_STUDENTS) {
            return null;
        }
        const studentName = studentCertificates.studentList.find(student => student.id === selectedOption.value);
        return `${studentName.firstName}_${studentName.lastName}`;
    }

    render() {
        const {
            selectedClassroom,
            studentCertificates,
            selectedDateRange,
            academicYear,
            updateSelectedDateRange,
            localeData
        } = this.props;
        const {
            templates,
            studentList,
            selectedOption,
            studentTotalNewCertificates,
            studentTotalPastCertificates
        } = this.state;

        if (!selectedClassroom || !studentList || !selectedOption) {
            return null;
        }

        const certificateLabel = localeData['Label - Certificates'].toLowerCase();
        return (
            <div className={css.component}>
                <Header title={localeData['Header - ClassDetails']} />
                <div className={css['main-content']}>
                    <div className={css.header}>
                        <h2>{selectedClassroom.class} {certificateLabel}</h2>
                        <div className={css['sub-header']}>
                            <div className={css['filter-container']}>
                                <div className="student-filter">
                                    <DropdownMenu
                                        className={css['student-dropdown']}
                                        options={studentList}
                                        selectedOption={selectedOption}
                                        onSelectOption={this.selectOption}
                                    />
                                </div>
                                <div className={css['date-filter']}>
                                    <DatePicker
                                        currentDate={selectedDateRange}
                                        academicYear={academicYear}
                                        onDateChange={updateSelectedDateRange}
                                        localeData={localeData}
                                        isLeft
                                    />
                                </div>
                            </div>
                            <div className={css['class-total']}>
                                <span>{`${localeData['Label - Total']}: `}
                                    {studentTotalNewCertificates + studentTotalPastCertificates}</span>
                            </div>
                        </div>
                    </div>
                    {templates.certificates.length > 0 &&
                        <CertificatesWrapper
                            heading={localeData['Label - NewCertificates']}
                            onDownload={() => {
                                this.showPrintTypeModal({
                                    content: `${localeData['PrintModal - SelectVersion']}`,
                                    total: studentTotalNewCertificates,
                                    isDownloadCertificateType: false
                                });
                            }}
                            downloadDisabled={studentTotalNewCertificates === 0}
                            downloadText={localeData['Label - DownloadAll']}
                            templateType={ALL_NEW}
                        >
                            {templates.certificates.map(template => (
                                <CertificateTemplate
                                    key={template.type}
                                    template={template}
                                    schoolType={studentCertificates.schoolType}
                                    onDownload={() =>
                                        this.showPrintTypeModal({
                                            content: `${localeData['PrintModal - SelectRankVersion']}`,
                                            total: template.total,
                                            certificateRank: CERT_VALUES[template.type],
                                            isDownloadCertificateType: true
                                        })}
                                />
                            ))}
                        </CertificatesWrapper>}
                    {templates.pastCertificates.length > 0 &&
                        <CertificatesWrapper
                            heading={localeData['Label - PastCertificates']}
                            onDownload={this.downloadAllPastCertificates}
                            downloadDisabled={studentTotalPastCertificates === 0}
                            downloadText={localeData['Label - DownloadAll']}
                            templateType={ALL_PAST}
                        >
                            {templates.pastCertificates.map(template => (
                                <CertificateTemplate
                                    key={template.type}
                                    template={template}
                                    schoolType={studentCertificates.schoolType}
                                    onDownload={() => this.downloadPastCertificatesByType({
                                        certificateType: CERT_VALUES[template.type],
                                        downloadType: template.type
                                    })}
                                />
                            ))}
                        </CertificatesWrapper>}
                </div>
                <Footer>
                    <Button
                        text={localeData['Label - Back']}
                        type={Button.LOZENGE}
                        size={Button.LARGE}
                        primary
                        onClick={this.onBackClick}
                    />
                </Footer>
            </div>
        );
    }
}

export const PastCertificatesTypes = PropTypes.shape({
    gold: PropTypes.number,
    silver: PropTypes.number,
    bronze: PropTypes.number,
    goldBars: PropTypes.number,
    total: PropTypes.number
});

export const CertificatesTypes = PropTypes.shape({
    bronze: PropTypes.number,
    silver: PropTypes.number,
    gold: PropTypes.number,
    platinum: PropTypes.number,
    emerald: PropTypes.number,
    diamond: PropTypes.number,
    epic: PropTypes.number,
    legend: PropTypes.number,
    total: PropTypes.number
});

Certificates.propTypes = {
    studentCertificates: PropTypes.shape({
        schoolType: PropTypes.oneOf([PRIMARY, SECONDARY]),
        allStudents: PropTypes.shape({
            total: PropTypes.number,
            pastCertificates: PastCertificatesTypes,
            certificates: CertificatesTypes
        }),
        studentList: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.number,
            firstName: PropTypes.string,
            lastName: PropTypes.string,
            pastCertificates: PastCertificatesTypes,
            certificates: CertificatesTypes
        }))
    }),
    selectedDateRange: PropTypes.shape({
        type: PropTypes.string,
        startDate: PropTypes.string,
        endDate: PropTypes.string
    }),
    academicYear: PropTypes.shape({
        startDate: PropTypes.string,
        endDate: PropTypes.string
    }),
    selectedStudent: PropTypes.shape({
        id: PropTypes.number,
        student: PropTypes.string
    }),
    selectedClassroom: PropTypes.shape({
        id: PropTypes.number,
        class: PropTypes.string
    }),
    productId: PropTypes.string,
    getStudentCertificates: PropTypes.func,
    getStudentCertificatesGemCount: PropTypes.func,
    downloadCertificatesByGem: PropTypes.func,
    downloadAllNewCerts: PropTypes.func,
    downloadNewCertsByType: PropTypes.func,
    downloadPastCertsByType: PropTypes.func,
    downloadAllPastCerts: PropTypes.func,
    openModal: PropTypes.func,
    updateSelectedDateRange: PropTypes.func,
    localeData: PropTypes.object,
    studentCertificatesGemCount: PropTypes.shape({
        retrievedGems: PropTypes.array,
        selectedGems: PropTypes.object
    })
};

/*
 * mapStateToProps - set component properties from current state provided by the Reducers.
 * @param state {object} - the revised state from CertificatesCombinedReducers.
 */
function mapStateToProps(state) {
    return {
        localeData: state.localeData,
        selectedDateRange: state.selectedDateRange,
        selectedClassroom: state.selectedClass,
        productId: state.productId,
        selectedStudent: state.selectedStudent,
        studentCertificates: state.studentCertificates,
        studentCertificatesGemCount: state.studentCertificatesGemCount,
        academicYear: state.academicYear
    };
}

/*
 * mapDispatchToProps - ensure that the outputs of the Action Creators are passed to the Reducers
 * @param dispatch
 */
function mapDispatchToProps(dispatch) {
    return bindActionCreators({
        openModal: modalActions.openModal,
        getStudentCertificates: studentCertificatesActions.getStudentCertificates,
        getStudentCertificatesGemCount: studentCertificatesGemCountActions.getStudentCertificatesGemCount,
        downloadCertificatesByGem: studentCertificatesGemCountActions.downloadCertificatesByGem,
        updateSelectedDateRange: datePickerActions.updateSelectedDateRange,
        downloadAllPastCerts: downloadActions.downloadAllPastCerts,
        downloadPastCertsByType: downloadActions.downloadPastCertsByType,
        downloadAllNewCerts: downloadActions.downloadAllNewCerts,
        downloadNewCertsByType: downloadActions.downloadNewCertsByType
    }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(styleable(css)(Certificates));



// WEBPACK FOOTER //
// ./containers/certificates/certificates.js